import { useCallback } from "react";
import { type FetchQueryOptions, useQueryClient } from "@tanstack/react-query";

// Module-level WeakMap for debounce timers, keyed by DOM element.
// WeakMap ensures automatic cleanup when elements are unmounted/GC'd.
// Inspired by TanStack Router's preload-on-intent pattern.
const timeoutMap = new WeakMap<EventTarget, ReturnType<typeof setTimeout>>();

interface UsePrefetchHandlersOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queries: Array<FetchQueryOptions<any, any, any, any>>;
  enabled?: boolean;
  delay?: number;
}

/**
 * Returns `onMouseEnter` and `onMouseLeave` handlers that prefetch
 * React Query queries on hover intent.
 *
 * With `delay > 0`, prefetching is debounced: leaving the element
 * before the delay cancels the prefetch. With `delay === 0`,
 * prefetching fires immediately on mouse enter.
 */
export function usePrefetchHandlers({
  queries,
  enabled = true,
  delay = 50,
}: UsePrefetchHandlersOptions) {
  const queryClient = useQueryClient();

  const doPrefetch = useCallback(() => {
    for (const query of queries) {
      queryClient.prefetchQuery(query);
    }
  }, [queryClient, queries]);

  const onMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled) {
        return;
      }

      if (!delay) {
        doPrefetch();
        return;
      }

      const target = event.currentTarget;
      if (timeoutMap.has(target)) {
        return;
      }

      const timeoutId = setTimeout(() => {
        timeoutMap.delete(target);
        doPrefetch();
      }, delay);
      timeoutMap.set(target, timeoutId);
    },
    [enabled, delay, doPrefetch],
  );

  const onMouseLeave = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled || !delay) {
        return;
      }

      const target = event.currentTarget;
      const timeoutId = timeoutMap.get(target);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutMap.delete(target);
      }
    },
    [enabled, delay],
  );

  return { onMouseEnter, onMouseLeave };
}
